import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import type {
  MCPServer,
  Conversation,
  ArtifactLineageGraph,
  MessageFeedback,
  MessageFeedbackCreate,
  Skill,
  SkillCreate,
  SkillUpdate,
  SkillsListResponse,
} from "~/components/assistant/types";

// Dataset Assistant application ID
const appId = "DATASET_ASSISTANT";

/**
 * Parse API error responses to extract human-readable error messages.
 */
async function parseApiError(response: Response, fallbackMessage: string): Promise<string> {
  try {
    const text = await response.text();
    const body = JSON.parse(text);

    // Response may have detail directly or wrapped in message
    const content = body.detail ?? body.message ?? "";

    // Handle 409 duplicate name conflict with friendly message
    if (response.status === 409) {
      const match = content.match(/name '([^']+)'/i);
      if (match) {
        return `A skill named "${match[1]}" already exists. Please choose a different name.`;
      }
    }

    return content || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
}

/**
 * Creates an assistant API implementation for dataset context.
 * Uses the dataset assistant API endpoint.
 */
export function createDatasetAssistantApi(baseUrl: string): AssistantApi {
  return {
    async listMcpServers(): Promise<MCPServer[]> {
      const response = await fetch(`${baseUrl}/mcp`, {
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(`Failed to load MCP servers: ${response.status}`);
      }

      return response.json();
    },

    async listConversations(datasetId: string): Promise<Conversation[]> {
      const params = new URLSearchParams({ dataset_id: datasetId, app_id: appId });
      const response = await fetch(`${baseUrl}/conversations?${params}`, {
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(`Failed to load conversations: ${response.status}`);
      }

      const data = await response.json();
      const conversations = data.items;

      // Sort by most recent first
      return conversations.sort(
        (a: Conversation, b: Conversation) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    },

    async createConversation(datasetId: string, datasetName?: string): Promise<Conversation> {
      const response = await fetch(`${baseUrl}/conversations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dataset_id: datasetId, app_id: appId, dataset_name: datasetName }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create conversation: ${response.status}`);
      }

      return response.json();
    },

    async deleteConversation(conversationId: string): Promise<void> {
      const response = await fetch(`${baseUrl}/conversations/${conversationId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete conversation: ${response.status}`);
      }
    },

    async updateConversation(
      conversationId: string,
      update: { display_name: string }
    ): Promise<Conversation> {
      const response = await fetch(`${baseUrl}/conversations/${conversationId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(update),
      });

      if (!response.ok) {
        throw new Error(`Failed to update conversation: ${response.status}`);
      }

      return response.json();
    },

    async loadChatHistory(conversationId: string): Promise<Response> {
      const response = await fetch(`${baseUrl}/conversations/${conversationId}/messages`, {
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(`Failed to load chat history: ${response.status}`);
      }

      return response;
    },

    async sendMessage(
      conversationId: string,
      params: {
        prompt: string;
        servers: string[];
        contextId: string;
        clientMessageId?: string;
      }
    ): Promise<Response> {
      const response = await fetch(`${baseUrl}/conversations/${conversationId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: params.prompt,
          servers: params.servers,
          client_message_id: params.clientMessageId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status}`);
      }

      return response;
    },

    async cancelMessage(conversationId: string): Promise<void> {
      const response = await fetch(`${baseUrl}/conversations/${conversationId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "MESSAGE_CANCELLED" }),
      });

      if (!response.ok) {
        throw new Error(`Failed to cancel message: ${response.status}`);
      }
    },

    async getArtifactLineage(
      conversationId: string,
      artifactId: string
    ): Promise<ArtifactLineageGraph> {
      const response = await fetch(
        `${baseUrl}/conversations/${conversationId}/artifacts/${artifactId}/lineage`,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load artifact lineage: ${response.status}`);
      }

      return response.json();
    },

    async putMessageFeedback(
      conversationId: string,
      messageId: string,
      feedback: MessageFeedbackCreate
    ): Promise<MessageFeedback> {
      const response = await fetch(
        `${baseUrl}/conversations/${conversationId}/messages/${messageId}/feedback`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(feedback),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to create message feedback: ${response.status}`);
      }

      return response.json();
    },

    async getMessageFeedback(
      conversationId: string,
      messageId: string
    ): Promise<MessageFeedback | null> {
      const response = await fetch(
        `${baseUrl}/conversations/${conversationId}/messages/${messageId}/feedback`,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to get message feedback: ${response.status}`);
      }

      return response.json();
    },

    async deleteMessageFeedback(conversationId: string, messageId: string): Promise<void> {
      const response = await fetch(
        `${baseUrl}/conversations/${conversationId}/messages/${messageId}/feedback`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }
      );

      // Treat 404 as success - feedback is already gone (idempotent delete)
      if (response.status === 404) {
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to delete message feedback: ${response.status}`);
      }
    },

    async listSkills(): Promise<SkillsListResponse> {
      const response = await fetch(`${baseUrl}/skills/`, {
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(`Failed to load skills: ${response.status}`);
      }

      return response.json();
    },

    async getSkill(skillId: string): Promise<Skill> {
      const response = await fetch(`${baseUrl}/skills/${skillId}`, {
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(`Failed to load skill: ${response.status}`);
      }

      return response.json();
    },

    async createSkill(skill: SkillCreate): Promise<Skill> {
      const response = await fetch(`${baseUrl}/skills/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(skill),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response, "Failed to create skill");
        throw new Error(errorMessage);
      }

      return response.json();
    },

    async updateSkill(skillId: string, skill: SkillUpdate): Promise<Skill> {
      const response = await fetch(`${baseUrl}/skills/${skillId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(skill),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response, "Failed to update skill");
        throw new Error(errorMessage);
      }

      return response.json();
    },

    async deleteSkill(skillId: string): Promise<void> {
      const response = await fetch(`${baseUrl}/skills/${skillId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete skill: ${response.status}`);
      }
    },
  };
}
